//#region IMR Imports

import { IIMRPopover, IMRPopoverOptions } from "@imr/controls.angularjs/components/overlay/popover"
import { IIMRPopup, IMRPopupOptions } from "@imr/controls.angularjs/components/overlay/popup"

//#endregion

import { TypeHelper } from "../../../helpers"

import { IMRBaseOverlayControlOptions } from "./base.overlay.control.interfaces"
import { IMRBaseControlController } from "./base.control.controller"

/**
 * Base Angular controller for popup components.
 * Should be used to standardize the way popups are implemented and how their content is loaded.
 * 
 * @example
 * 
 * Basic usage:
 * 
 * export class MyComponentController
 *      extends
 *          IMRBasePopupControlController<IComponentInterface, ComponentOptions>
 *      implements
 *          IComponentInterface {
 * 
 *  //#region Lifecycle
 * 
 *  protected initialize(options: ComponentOptions): JQueryPromise<void> {
 * 
 *      // Inline components initialization goes here
 *      // You should call super.initialize here
 * 
 *      return super.initialize(options)
 *  }
 * 
 *  protected initializePopup(popupOptions: IMRPopupOptions): void {
 *       
 *      // Popup options customization goes here
 *  }
 * 
 *   protected initializePopupContent(): JQueryPromise<void> {
 *       
 *      // Inner components initialization goes here
 *   }
 *   
 *   //#endregion
 *
 */
export abstract class IMRBaseOverlayControlController<TComponent, TOptions extends IMRBaseOverlayControlOptions<TComponent, any>>
    extends
    IMRBaseControlController<TComponent, TOptions> {

    //#region Members

    /**
     * Popup instance
     */
    protected popup: IIMRPopup
    protected popover: IIMRPopover
    protected popupOptions: IMRPopupOptions
    protected popoverOptions: IMRPopoverOptions

    private isOverlayContentInitialized: boolean

    //#endregion


    //#region Lifecycle

    /**
     * Called when popup is being configured.
     * Can be used to customize popup options.
     * 
     * @param popupOptions
     */
    protected customizePopoverOverlay(overlayOptions: IMRPopoverOptions): void { }

    /**
     * Called when popup is being configured.
     * Can be used to customize popup options.
     * 
     * @param popupOptions
     */
    protected customizePopupOverlay(overlayOptions: IMRPopupOptions): void { }

    /**
     * Called after the popup has been shown.
     * Can be used to initialized popup's inner components.
     * The onPopupContentInitialized event is fired after this functions returns or the promise resolves.
     * 
     * @returns Nothing or a Promise that should resolve after every component has initialized.
     */
    protected abstract initializeOverlayContent(): JQueryPromise<void> | void

    protected initialize(options: TOptions): JQueryPromise<any> {
        this.isOverlayContentInitialized = false
        this.popupOptions = TypeHelper.clone(options.popupOptions, true) as IMRPopupOptions
        this.popoverOptions = TypeHelper.clone(options.popoverOptions, true) as IMRPopoverOptions

        if (this.popupOptions != null) {
            if (this.customizePopupOverlay != null) {
                this.customizePopupOverlay(this.popupOptions)
            }

            if (this.popupOptions.innerOptions == null) {
                this.popupOptions.innerOptions = {}
            }

            this.popupOptions.innerOptions.onShowing = TypeHelper.wrapAndCall(this.popupOptions.innerOptions.onShowing, () => {
                this.onOverlayShowing()
            })

            return super
                .waitForOnInitialized(this.popupOptions)
                .then((e) => {
                    this.popup = e.component
                })
        }
        else if (this.popoverOptions != null) {
            if (this.customizePopoverOverlay != null) {
                this.customizePopoverOverlay(this.popoverOptions)
            }

            if (this.popoverOptions.innerOptions == null) {
                this.popoverOptions.innerOptions = {}
            }

            this.popoverOptions.innerOptions.onShowing = TypeHelper.wrapAndCall(this.popoverOptions.innerOptions.onShowing, () => {
                this.onOverlayShowing()
            })

            return super
                .waitForOnInitialized(this.popoverOptions)
                .then((e) => {
                    this.popover = e.component
                })
        }
    }

    private onOverlayShowing(): void {
        if (this.isOverlayContentInitialized === false) {
            const initializeResult = this.initializeOverlayContent()

            if (TypeHelper.isPromise(initializeResult)) {
                (initializeResult as JQueryPromise<void>)
                    .then(() => {
                        this.onOverlayContentInitialized()
                    })
            }
            else {
                this.onOverlayContentInitialized()
            }
        }

        this.isOverlayContentInitialized = true
    }

    //#endregion


    //#region Events

    private onOverlayContentInitialized(): void {
        if (this.options != null && this.options.onOverlayContentInitialized != null) {
            this.options.onOverlayContentInitialized()
        }
    }

    //#endregion
}